"use client";

import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";

// Dynamic import with loading fallback — prevents React error #130
// during client-side navigation when the dashboard chunk hasn't loaded yet
const CopytradingDashboard = dynamic(() => import("./CopytradingDashboard"), {
  ssr: false,
  loading: () => null,
});

/**
 * Client wrapper that handles auth state for copytrading page.
 * Uses optimistic UI - if user was logged in before, shows dashboard immediately.
 */
export default function CopytradingContent() {
  const { isLoggedIn } = useAuth();
  const [mounted, setMounted] = useState(false);

  // Dev-only: ?mockTraders=1 or ?mockExecs=1 (localhost) mounts the
  // dashboard WITHOUT a login so the Traders / Recent Trades layouts can
  // be previewed with seeded mock data. NODE_ENV gate keeps it out of
  // prod. Evaluated post-mount only (window guard), so SSR/first paint
  // is unaffected.
  const isMockPreview =
    mounted &&
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    (new URLSearchParams(window.location.search).get("mockTraders") === "1" ||
      new URLSearchParams(window.location.search).get("mockExecs") === "1");

  useEffect(() => {
    setMounted(true);
  }, []);

  // Hide the landing when logged in (optimistic). The first-paint hide
  // is already handled by the inline <script> in page.tsx that injects
  // `#__ff-copytrading-hide-landing` based on the localStorage flag;
  // this effect is the runtime mirror: keep the inline element hidden
  // while authed, restore it (and tear down the style tag) if Privy
  // ever resolves us back to logged-out.
  useEffect(() => {
    if (!mounted) return;

    const landing = document.querySelector(".copytrading-landing");
    const inlineStyle = document.getElementById("__ff-copytrading-hide-landing");

    if (landing) {
      if (isLoggedIn || isMockPreview) {
        (landing as HTMLElement).style.display = "none";
      } else {
        (landing as HTMLElement).style.display = "block";
        // Stale optimistic flag from a prior session — rip out the
        // pre-paint style tag so the landing is actually visible to
        // the logged-out user instead of an empty page.
        if (inlineStyle) inlineStyle.remove();
      }
    }
  }, [mounted, isLoggedIn, isMockPreview]);

  if (!mounted) {
    return null;
  }

  if (!isLoggedIn && !isMockPreview) {
    return null;
  }

  return <CopytradingDashboard />;
}
